import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowRight, Phone, CalendarClock, CheckCircle2 } from "lucide-react";
import { useBookCall } from "@/components/BookCallContext";
import { TESTIDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "+91 97692 32430";

const initial = {
    name: "",
    company: "",
    email: "",
    phone: "",
    requirement: "",
};

function validate(f) {
    const e = {};
    if (!f.name.trim()) e.name = "Required";
    if (!f.company.trim()) e.company = "Required";
    if (!f.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Invalid email";
    if (!f.phone.trim()) e.phone = "Required";
    if (!f.requirement.trim()) e.requirement = "Tell us what to build";
    return e;
}

export default function BookCallModal() {
    const { open, closeModal, context } = useBookCall();
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    // Reset on close
    useEffect(() => {
        if (!open) {
            const t = setTimeout(() => {
                setForm(initial);
                setErrors({});
                setDone(false);
            }, 250);
            return () => clearTimeout(t);
        }
    }, [open]);

    // ESC to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && closeModal();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, closeModal]);

    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        const v = validate(form);
        setErrors(v);
        if (Object.keys(v).length) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/bookings`, {
                ...form,
                source: context.source || "book-a-call",
                use_case_id: context.use_case_id || null,
            });
            setDone(true);
            toast.success("Request received, we'll be in touch.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={closeModal}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(8,9,12,0.55)",
                        backdropFilter: "blur(4px)",
                        zIndex: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 20,
                    }}
                >
                    <motion.div
                        data-testid={TESTIDS.book.modal}
                        initial={{ opacity: 0, scale: 0.97, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97, y: 10 }}
                        transition={{ duration: 0.22 }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#0e1116",
                            color: "var(--ink)",
                            borderRadius: 18,
                            width: "100%",
                            maxWidth: 460,
                            maxHeight: "92vh",
                            overflow: "auto",
                            border: "1px solid var(--line-strong)",
                            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
                        }}
                    >
                        <div
                            style={{
                                background: "linear-gradient(135deg, #1a2b5c, #0e1d44)",
                                color: "#fff",
                                padding: "18px 22px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid var(--line-strong)",
                            }}
                        >
                            <b className="font-display" style={{ fontSize: "1.05rem", fontWeight: 600 }}>
                                Book a Demo
                            </b>
                            <button
                                data-testid={TESTIDS.book.close}
                                onClick={closeModal}
                                aria-label="Close"
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#fff",
                                    cursor: "pointer",
                                    padding: 4,
                                    opacity: 0.85,
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div style={{ padding: 22 }}>
                            {done ? (
                                <div data-testid={TESTIDS.book.success}>
                                    <CheckCircle2 size={28} style={{ color: "var(--accent)" }} />
                                    <h3 className="h3" style={{ margin: "10px 0 8px", fontSize: "1.3rem" }}>
                                        Got it, we&apos;ll be in touch.
                                    </h3>
                                    <p style={{ color: "var(--kl-muted)", fontSize: ".92rem" }}>
                                        A KyroLabs team member will reach out within one business day to
                                        confirm your slot or call back.
                                    </p>
                                    <div
                                        className="callnow"
                                        style={{
                                            background: "#0e1116",
                                            color: "#fff",
                                            borderRadius: 10,
                                            padding: "12px 14px",
                                            fontSize: ".88rem",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginTop: 16,
                                        }}
                                    >
                                        <span>Talk now?</span>
                                        <a
                                            href={`tel:${PHONE.replace(/\s/g, "")}`}
                                            data-testid={TESTIDS.book.talkNow}
                                            style={{
                                                color: "#8fd0ff",
                                                textDecoration: "none",
                                                fontFamily: "JetBrains Mono, monospace",
                                            }}
                                        >
                                            ☏ {PHONE}
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={submit} noValidate>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                        <Field label="Name" error={errors.name}>
                                            <input
                                                data-testid={TESTIDS.book.name}
                                                value={form.name}
                                                onChange={set("name")}
                                                placeholder="Your name"
                                            />
                                        </Field>
                                        <Field label="Company" error={errors.company}>
                                            <input
                                                data-testid={TESTIDS.book.company}
                                                value={form.company}
                                                onChange={set("company")}
                                                placeholder="Company"
                                            />
                                        </Field>
                                        <Field label="Email" error={errors.email}>
                                            <input
                                                data-testid={TESTIDS.book.email}
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                placeholder="you@company.com"
                                            />
                                        </Field>
                                        <Field label="Phone" error={errors.phone}>
                                            <input
                                                data-testid={TESTIDS.book.phone}
                                                value={form.phone}
                                                onChange={set("phone")}
                                                placeholder="+91…"
                                            />
                                        </Field>
                                    </div>

                                    <div className="field" style={{ marginTop: 10 }}>
                                        <label>What you want to build</label>
                                        <textarea
                                            data-testid={TESTIDS.book.requirement}
                                            value={form.requirement}
                                            onChange={set("requirement")}
                                            rows={3}
                                            placeholder="Your requirement"
                                        />
                                        {errors.requirement && (
                                            <div style={{ color: "#c0392b", fontSize: ".74rem", marginTop: 4 }}>
                                                {errors.requirement}
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: 10,
                                            marginTop: 12,
                                            marginBottom: 6,
                                        }}
                                    >
                                        <PathBox
                                            icon={CalendarClock}
                                            title="① Pick a slot"
                                            line="Choose a time."
                                        />
                                        <PathBox
                                            icon={Phone}
                                            title="② Or submit"
                                            line="We'll call back."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        data-testid={TESTIDS.book.submit}
                                        disabled={submitting}
                                        className="btn btn-p"
                                        style={{
                                            width: "100%",
                                            justifyContent: "center",
                                            marginTop: 10,
                                            opacity: submitting ? 0.65 : 1,
                                        }}
                                    >
                                        {submitting ? "Sending…" : "Request a call"}
                                        <ArrowRight size={14} />
                                    </button>

                                    <div
                                        className="callnow"
                                        style={{
                                            background: "#0e1116",
                                            color: "#fff",
                                            borderRadius: 10,
                                            padding: "10px 13px",
                                            fontSize: ".84rem",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginTop: 14,
                                        }}
                                    >
                                        <span>Talk now?</span>
                                        <a
                                            href={`tel:${PHONE.replace(/\s/g, "")}`}
                                            data-testid={TESTIDS.book.talkNow}
                                            style={{
                                                color: "#8fd0ff",
                                                textDecoration: "none",
                                                fontFamily: "JetBrains Mono, monospace",
                                            }}
                                        >
                                            ☏ {PHONE}
                                        </a>
                                    </div>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Field({ label, error, children }) {
    return (
        <div className="field" style={{ marginBottom: 0 }}>
            <label>{label}</label>
            {children}
            {error && (
                <div style={{ color: "#c0392b", fontSize: ".74rem", marginTop: 4 }}>{error}</div>
            )}
        </div>
    );
}

function PathBox({ icon: Icon, title, line }) {
    return (
        <div
            style={{
                border: "1px dashed var(--line-strong)",
                borderRadius: 10,
                padding: 12,
                background: "rgba(255,255,255,0.02)",
                fontSize: ".78rem",
                color: "var(--kl-muted)",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Icon size={13} style={{ color: "var(--accent)" }} />
                <b style={{ color: "var(--ink)", fontFamily: "Bricolage Grotesque" }}>{title}</b>
            </div>
            {line}
        </div>
    );
}
